/**
 * EventManifest — the group's travel, read the way a trip is actually lived.
 *
 * ── The redesign, 2026-08-18 ────────────────────────────────────────────────
 * Wade: *"Manifest should show focus flights and booking status (clearly realized
 * booked). Manifest UI should be like itinerary with all outbound on top and
 * returns on bottom with an ability to add hotel details for each with event
 * showing and like itins able to add other activity before/after (lots of stuff to
 * display so perhaps simple '+' before/after could work)."*
 *
 * So the page is an ITINERARY for a group, not a list of people:
 *
 *   Getting there  → every outbound leg, whoever is on it, earliest first
 *   Own transport  → § G33, the people who are not flying at all
 *   On the ground  → the meeting itself, then each traveller's hotel + plans
 *   Heading home   → every return leg
 *
 * The per-PERSON shape it replaced answered the ROSTER's question ("is Sarah
 * sorted?"). A manifest is read at a moment — everyone arriving, the day, everyone
 * leaving — so direction is the axis and the name lives inside the row.
 *
 * ── What must NOT change ────────────────────────────────────────────────────
 *  · § G16 — one page, everyone, no tabs. Print via `@media print` (the browser is
 *    the export; no PDF library ships). "Copy as text" for people who will never
 *    open the app.
 *  · § G16 — visible to every group MEMBER, not organizers only. § G6 — fares and
 *    add-on costs arrive already stripped for a non-organizer; this page never
 *    decides that.
 *  · B4 — a booked leg's flight comes from the booking record or stays BLANK. The
 *    focus flight is shown as its own, labelled fact and never promoted.
 *  · § G33 — a driver is complete, not an empty flight row.
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Car,
  Check,
  Copy,
  ExternalLink,
  MapPin,
  PlaneLanding,
  PlaneTakeoff,
  Printer,
  Star,
  Users,
} from 'lucide-react';
import { travelGroupApi, type GroupManifest, type ManifestItem } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { anchorHeadline } from '@/components/trips/organizerRosterView';
import ManifestLegRow from '@/components/events/manifest/ManifestLegRow';
import ManifestStayCard from '@/components/events/manifest/ManifestStayCard';
import ManifestItemDialog, {
  type ManifestItemTarget,
} from '@/components/events/manifest/ManifestItemDialog';
import type { ItineraryItemType } from '@/lib/types';
import {
  manifestSections,
  manifestText,
  meetingDay,
  personLabel,
  travelModeText,
} from './eventManifestView';

/** A section heading in the itinerary's idiom: icon tile, title, count. */
function Band({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof PlaneLanding;
  title: string;
  count?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/[0.1] text-[hsl(var(--accent-foreground))]">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        {count && <span className="text-xs text-muted-foreground">{count}</span>}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export default function EventManifest() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [target, setTarget] = useState<ManifestItemTarget | null>(null);

  const { data: m, isLoading, isError } = useQuery({
    queryKey: ['event-manifest', id],
    queryFn: () => travelGroupApi.manifest(id!),
    enabled: !!id,
    retry: false,
  });

  const removal = useMutation({
    mutationFn: (v: { memberId: string; item: ManifestItem }) =>
      travelGroupApi.removeManifestItem(id!, v.memberId, v.item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-manifest', id] }),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-6 py-8">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError || !m) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 px-6 py-16 text-center">
        <AlertTriangle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">This manifest isn&apos;t available to you.</p>
        <Link to="/trips" className="text-xs underline">
          Back to your trips
        </Link>
      </div>
    );
  }

  // ⚠️ `label: null` on purpose. `anchorHeadline` PREFIXES the anchor label when
  // given one, and the label already titles the meeting card below — a walk on
  // 2026-08-18 showed "Cxp/GR planning session" printed twice inside that card,
  // and a third time under the event's own name in the header. Each element says
  // one thing: the label titles the meeting, `when` says when it is.
  const when = anchorHeadline({
    start_at: m.event.anchor_start_at,
    end_at: m.event.anchor_end_at,
    timezone: m.event.anchor_timezone,
    label: null,
  });
  // The pasted text has no card around it, so THERE the label belongs on the line.
  const whenWithLabel = anchorHeadline({
    start_at: m.event.anchor_start_at,
    end_at: m.event.anchor_end_at,
    timezone: m.event.anchor_timezone,
    label: m.event.anchor_label,
  });

  const s = manifestSections(m as GroupManifest);
  // The meeting's own day, so "Add hotel" opens on the night people are there
  // rather than on today. Falls back to the first outbound date we know of.
  const groundDay =
    meetingDay(m.event.anchor_start_at, m.event.anchor_timezone) ??
    (s.outbound[0]?.leg.booking_departure_date || s.outbound[0]?.leg.departure_date) ??
    null;

  // Writing is offered to whoever the server would let write. It answers that
  // with a stated 403 rather than a silent no-op, and fares being present is the
  // one signal the payload already carries for "this caller runs the event".
  const canWrite = m.people.some((p) => p.member_id !== null);
  const openAdd = canWrite
    ? (memberId: string, dayDate: string, itemType: ItineraryItemType) => {
        const person = m.people.find((p) => p.member_id === memberId);
        setTarget({ memberId, personName: person?.display_name ?? 'this traveller', dayDate, itemType });
      }
    : undefined;

  const bookedPct = m.total_legs > 0 ? Math.round((m.booked_legs / m.total_legs) * 100) : 0;
  const allBooked = m.total_legs > 0 && m.booked_legs === m.total_legs;
  // ⚠️ NOT `m.summary`. That string ends with "N of M legs booked", which is
  // exactly what the pill beside it says — the walk showed "4 OF 6 LEGS BOOKED
  // · … · 4 of 6 legs booked" on one line. The pill owns the legs; this line
  // carries the two facts the pill does not, and nothing twice.
  const peopleLine =
    `${m.people.filter((p) => p.complete).length} of ${m.people.length} fully booked · ` +
    `${m.travellers} travelling`;

  function copy() {
    navigator.clipboard.writeText(manifestText(m as GroupManifest, whenWithLabel)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const peopleWithGround = m.people.filter(
    (p) => p.items.length > 0 || (p.travel_mode ?? 'flight') === 'flight' || p.trip_id
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 print:px-0 print:py-0">
      <style>{`@media print { @page { margin: 14mm; } .no-print { display: none !important; } body { background: #fff; } }`}</style>

      <div className="no-print mb-4 flex flex-wrap items-center gap-3">
        <Link
          to={`/events/${id}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to the meeting
        </Link>
        <button
          onClick={() => window.print()}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs hover:bg-muted"
        >
          <Printer className="h-3.5 w-3.5" /> Print / save as PDF
        </button>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs hover:bg-muted"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy as text'}
        </button>
      </div>

      {/* ── Header: what, when, where, and how much of it is real ───────── */}
      <header className="border-b border-border pb-4">
        <h1 className="text-xl font-semibold">{m.event.name}</h1>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          <span>{when}</span>
          {m.event.venue && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" aria-hidden="true" /> {m.event.venue}
            </span>
          )}
          {m.event.destination_airport && <span>· {m.event.destination_airport}</span>}
        </p>
        {m.event.event_url && (
          <a
            href={m.event.event_url}
            className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground underline"
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink className="h-3 w-3" /> Event details
          </a>
        )}

        {/* "Clearly realized booked", at the whole-event scale. The bar is the
            glance; the sentence is the truth; neither relies on colour alone. */}
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span
              className={
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white ' +
                (allBooked ? 'bg-emerald-600' : 'bg-amber-500')
              }
            >
              {allBooked ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
              {m.total_legs === 0 ? 'nothing booked yet' : `${m.booked_legs} of ${m.total_legs} legs booked`}
            </span>
            <span className="text-muted-foreground">{peopleLine}</span>
          </div>
          <div
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={`${bookedPct}% of legs booked`}
          >
            <div
              className={'h-full rounded-full ' + (allBooked ? 'bg-emerald-600' : 'bg-amber-500')}
              style={{ width: `${bookedPct}%` }}
            />
          </div>
        </div>
      </header>

      {m.people.length === 0 ? (
        <div className="mt-8 flex flex-col items-center gap-3 rounded-xl border border-dashed py-12 text-center">
          <Users className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium">Nobody is on this event yet</p>
            <p className="text-xs text-muted-foreground">
              Add attendees on the meeting page and their flights will appear here.
            </p>
          </div>
          <Link to={`/events/${id}`} className="text-xs underline">
            Go to the meeting
          </Link>
        </div>
      ) : (
        <>
          {/* ── Getting there ─────────────────────────────────────────────── */}
          <Band
            icon={PlaneLanding}
            title="Getting there"
            count={s.outbound.length > 0 ? `${s.outbound.length} arriving` : undefined}
          >
            {s.outbound.length === 0 && s.unplanned.length === 0 && (
              <p className="text-xs text-muted-foreground">No outbound flights planned yet.</p>
            )}
            {s.outbound.map((row, i) => (
              <ManifestLegRow key={`${row.person.member_id ?? row.person.display_name}-out-${i}`} row={row} onAdd={openAdd} />
            ))}
            {s.unplanned.length > 0 && (
              <div className="break-inside-avoid rounded-lg border border-dashed border-amber-300 bg-amber-50/[0.35] px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  No flights planned yet
                </p>
                <ul className="mt-1 space-y-0.5">
                  {s.unplanned.map((p) => (
                    <li key={p.member_id ?? p.display_name} className="text-sm">
                      {personLabel(p)}
                      {p.home_airport && (
                        <span className="ml-1.5 text-xs text-muted-foreground">from {p.home_airport}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Band>

          {/* ── § G33: not everybody flies ────────────────────────────────── */}
          {s.ownTransport.length > 0 && (
            <Band icon={Car} title="Own transport" count={`${s.ownTransport.length} not flying`}>
              {s.ownTransport.map((p) => (
                <div
                  key={p.member_id ?? p.display_name}
                  className="break-inside-avoid rounded-lg border border-l-4 border-border border-l-emerald-500 bg-card px-3 py-2.5 shadow-paper"
                  aria-label={`${p.display_name} is not flying: ${travelModeText(p.travel_mode)}`}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold">{personLabel(p)}</span>
                    <span className="text-xs text-muted-foreground">{travelModeText(p.travel_mode)}</span>
                    {/* Sorted, not missing. This is the whole point of G33. */}
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      <Check className="h-3 w-3" aria-hidden="true" /> Sorted
                    </span>
                  </div>
                  {p.travel_mode_note && (
                    <p className="mt-1 text-xs text-muted-foreground">{p.travel_mode_note}</p>
                  )}
                </div>
              ))}
            </Band>
          )}

          {/* ── On the ground: the meeting, then each traveller's stay ────── */}
          <Band icon={Star} title="On the ground">
            <div className="break-inside-avoid rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-semibold">{m.event.anchor_label || m.event.name}</span>
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                  the meeting
                </span>
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{when}</span>
                {m.event.venue && <span>· {m.event.venue}</span>}
              </p>
            </div>

            {peopleWithGround.map((p) => (
              <ManifestStayCard
                key={p.member_id ?? p.display_name}
                person={p}
                items={p.items}
                defaultDate={groundDay}
                onAdd={openAdd}
                onDelete={
                  canWrite ? (memberId, item) => removal.mutate({ memberId, item }) : undefined
                }
                deletingId={removal.isPending ? (removal.variables?.item.id ?? null) : null}
              />
            ))}
          </Band>

          {/* ── Heading home ─────────────────────────────────────────────── */}
          <Band
            icon={PlaneTakeoff}
            title="Heading home"
            count={s.returns.length > 0 ? `${s.returns.length} departing` : undefined}
          >
            {s.returns.length === 0 ? (
              <p className="text-xs text-muted-foreground">No return flights planned yet.</p>
            ) : (
              s.returns.map((row, i) => (
                <ManifestLegRow key={`${row.person.member_id ?? row.person.display_name}-ret-${i}`} row={row} onAdd={openAdd} />
              ))
            )}
          </Band>
        </>
      )}

      {m.outstanding.length > 0 && (
        <section className="mt-6 border-t border-border pt-3">
          <h2 className="text-sm font-semibold">Still to book</h2>
          <ul className="mt-1 text-sm text-muted-foreground">
            {m.outstanding.map((o) => (
              <li key={o.display_name}>
                {o.display_name}
                {o.unbooked > 0 ? ` — ${o.unbooked} leg${o.unbooked === 1 ? '' : 's'}` : ' — no flights planned'}
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="mt-4 border-t border-border pt-3 text-sm">
        <span className="font-medium">Total: </span>
        {m.amount_label}
      </footer>

      {removal.isError && (
        <p role="alert" className="no-print mt-2 text-xs text-destructive">
          Couldn&apos;t remove that — {(removal.error as any)?.response?.data?.error ?? 'try again'}.
        </p>
      )}

      <ManifestItemDialog
        groupId={id!}
        target={target}
        onClose={() => setTarget(null)}
        defaultPlace={m.event.venue}
      />
    </div>
  );
}
